import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { BiImport } from "react-icons/bi";
import { FaPlus } from "react-icons/fa";
import { CiSearch } from "react-icons/ci";

// API imports (ensure these are correct for your project structure)
import partiesApi from '../data/party';
import stockesApi from '../data/stockhistory';
import stockdataApi from '../data/stock';

// --- Calculate total deducted (withdrawal + direct payment) for a given party ---
function calculateTotalDeductedIncludingDirect(
    partyStocks: any[],
    partyId: number | string
): number {
    let totalWithdrawals = 0;
    let totalDirectAmount = 0;
    for (const partyStock of partyStocks) {
        if (String(partyStock.party_id) !== String(partyId)) continue;
        let cats = partyStock.categories;
        if (typeof cats === "string") {
            try {
                cats = JSON.parse(cats);
            } catch {
                cats = [];
            }
        }
        // direct payments
        const directItems = Array.isArray(cats)
            ? cats.filter((cat: any) => cat.investment_type === "direct")
            : [];
        for (const d of directItems) {
            totalDirectAmount += Number(d.total_amount || 0);
        }
        // withdrawals
        const withdrawalItems = Array.isArray(cats)
            ? cats.filter((cat: any) => cat.investment_type === "withdrawal")
            : [];
        for (const w of withdrawalItems) {
            totalWithdrawals += Number(w.total_amount || 0);
        }
    }
    return totalWithdrawals + totalDirectAmount;
}

// --- Calculate total withdrawal for a given party (for explicit withdrawals, for balance sheet etc) ---
function calculateTotalWithdrawals(
    partyStocks: any[],
    partyId: number | string
): number {
    let totalWithdrawals = 0;
    for (const partyStock of partyStocks) {
        if (String(partyStock.party_id) !== String(partyId)) continue;
        let cats = partyStock.categories;
        if (typeof cats === "string") {
            try {
                cats = JSON.parse(cats);
            } catch {
                cats = [];
            }
        }
        const withdrawalItems = Array.isArray(cats)
            ? cats.filter((cat: any) => cat.investment_type === "withdrawal")
            : [];
        for (const w of withdrawalItems) {
            totalWithdrawals += Number(w.total_amount || 0);
        }
    }
    return totalWithdrawals;
}

interface Party {
    id: number;
    date: string;
    name: string;
    number: string;
    contact_person_name: string;
    contact_person_number: string;
    categories: string[];
    deleteparty: number;
    total_amount: number; // Opening balance / starting amount
}
interface PartyStockDBType {
    id: number | string;
    party_id: number | string;
    categories: string | any[];
}
interface Stock {
    id: number | string;
    party_id: number | string;
    date: string;
    total_amount: number | string;
    indirect_charges: number;
    categories: string[] | string;
    [key: string]: any;
}

function Partylist() {
    const [searchTerm, setSearchTerm] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [totalRows] = useState<number>(10);
    const [parties, setParties] = useState<Party[]>([]);
    const [partyStocks, setPartyStocks] = useState<PartyStockDBType[]>([]);
    const [stocks, setStocks] = useState<Stock[]>([]);
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();

    useEffect(() => {
        async function fetchAll() {
            setLoading(true);
            let partiesArr: Party[] = [];
            let partyStocksArr: PartyStockDBType[] = [];
            let stocksArr: Stock[] = [];
            try {
                const partiesRes = await partiesApi();
                partiesArr = Array.isArray(partiesRes) ? partiesRes : [];
            } catch {}
            try {
                const psRes = await stockesApi();
                partyStocksArr = Array.isArray(psRes) ? psRes : [];
            } catch {}
            try {
                const stRes = await stockdataApi();
                stocksArr = Array.isArray(stRes) ? stRes : [];
            } catch (err) {}
            setParties(partiesArr);
            setPartyStocks(partyStocksArr);
            setStocks(stocksArr);
            setLoading(false);
        }
        fetchAll();
    }, []);

    // Calculate per-party: opening, purchased, withdrawn/direct, remaining
    const partyTableData = useMemo(() => {
        if (!parties.length) return [];
        return parties.map((party) => {
            // Opening balance:
            const openingBalance = Number(party.total_amount) || 0;
            // All stocks for this party:
            const relatedStocks = stocks.filter(s => String(s.party_id) === String(party.id));
            // Calculate sum of purchases: for each stock, sum total_amount and indirect_charges from each category
            let purchaseAmount = 0;
            for (const s of relatedStocks) {
                // 'categories' can be a stringified JSON or an array
                let categoriesArr: any[] = [];
                if (typeof s.categories === "string") {
                    try {
                        categoriesArr = JSON.parse(s.categories);
                        // If parsed is not array (e.g. single object), make it an array
                        if (!Array.isArray(categoriesArr) && typeof categoriesArr === "object") {
                            categoriesArr = [categoriesArr];
                        }
                    } catch {
                        categoriesArr = [];
                    }
                } else if (Array.isArray(s.categories)) {
                    categoriesArr = s.categories;
                }
                // Sum up total_amount + indirect_charges from all categories in this stock
                for (const cat of categoriesArr) {
                    const ta = Number(cat.total_amount) || 0;
                    const ic = Number(cat.indirect_charges) || 0;
                    purchaseAmount += ta + ic;
                }
            }
            // All deducted (withdrawal+direct)
            const totalDeducted = calculateTotalDeductedIncludingDirect(partyStocks, party.id);
            // Remaining amount: opening + purchases - withdrawals/direct
            const remainingAmount = openingBalance  - purchaseAmount;
            return {
                party,
                openingBalance,
                purchaseAmount,
                totalDeducted,
                remainingAmount
            }
        });
    }, [parties, stocks, partyStocks]);

    // Search and paginate
    const filteredData = partyTableData.filter(({party}) =>
        party.name.toLowerCase().includes(searchTerm.toLowerCase())
    );
    const lastIndex = totalRows * currentPage;
    const firstIndex = lastIndex - totalRows;
    const currentDisplay = filteredData.slice(firstIndex, lastIndex);

    // --- BUTTON handlers: update to match your app's true routing, modals, or confirmation as needed
    const handleAddStock = (partyId: number | string) => navigate(`/add-stock?party=${partyId}`);
    const handleSellStock = (partyId: number | string) => navigate(`/sell-stock?party=${partyId}`);
    const handleWithdraw = (partyId: number | string) => navigate(`/withdraw?party=${partyId}`);
    const handleAddAmount = (partyId: number | string) => navigate(`/add-amount?party=${partyId}`);
    const handleUpdateStockValue = (partyId: number | string) => navigate(`/update-stock-value?party=${partyId}`);
    const handleViewHistory = (partyId: number | string) => navigate(`/usershistory?party=${partyId}`);
    const handleViewBalanceSheet = (partyId: number | string) => navigate(`/balancesheet?party=${partyId}`);
    const handleExport = (partyId: number | string) => {
        // For demo: export a CSV row for this party
        const data = partyTableData.find((pt) => pt.party.id === partyId);
        if (!data) return;
        const csv = [
            ["Party Name", "Opening", "Purchase", "Total Deducted", "Remaining"],
            [data.party.name, data.openingBalance, data.purchaseAmount, data.totalDeducted, data.remainingAmount]
        ].map(e => e.join(',')).join('\n');
        const blob = new Blob([csv], { type: "text/csv" });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `${data.party.name}_party_summary.csv`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
    };

    return (
        <div className='p-6'>
            <div className='w-full'>
                <div className='flex flex-wrap justify-between w-full'>
                    <div className='flex items-center gap-4'>
                        <h1 className='text-[28px] font-[500]'>Party list</h1>
                        <div className='bg-[#F9F5FF] p-2 rounded-[40px]'>
                            <span className='text-[#6941C6] text-[12px]'>{parties.length} vendors</span>
                        </div>
                    </div>
                    <div className='flex  items-center gap-4'>
                        <button className='flex items-center gap-1 border-[1px] border-[#D0D5DD bg-[#fff] py-2 px-4 rounded-[8px]'>
                            <BiImport /> Import
                        </button>
                        <Link to='/add-stock'>
                            <button className="flex items-center gap-2 text-[16px] font-[500] text-[#fff] bg-gradient-to-r from-[#1CAC70] to-[#FF9500] py-2 rounded-[8px] px-4">
                                <FaPlus />Add Stock
                            </button>
                        </Link>
                    </div>
                </div>
                <span className='text-[#667085] text-[14px]'>Keep track of party investments and balances.</span>
            </div>

            <div className='flex justify-between w-full mt-7'>
                <div className="w-[40%] bg-[#fff] h-[38px] flex items-center rounded-[8px] px-2 border-[#D0D5DD] border-[1px] gap-1 ">
                    <CiSearch className="text-[#8488B7] text-[24px] cursor-pointer" />
                    <input
                        type="text"
                        placeholder="Search..."
                        className="w-full bg-transparent h-[32px] focus:outline-none "
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                </div>
                <div />
            </div>

            <div className="max-w-full overflow-x-auto border border-gray-200 mt-4">
                <table className="w-full max-w-full border-collapse table-auto">
                    <thead className="bg-[#FCFCFD]">
                        <tr>
                            <th className="px-4 py-3 text-left text-sm font-semibold text-[#667085] border-b whitespace-nowrap">
                                Party Name
                            </th>
                            <th className="px-4 py-3 text-left text-sm font-semibold text-[#667085] border-b whitespace-nowrap">
                                Opening Amount
                            </th>
                            <th className="px-4 py-3 text-left text-sm font-semibold text-[#667085] border-b whitespace-nowrap">
                                Remaining Amount
                            </th>
                            <th className="px-4 py-3 text-left text-sm font-semibold text-[#667085] border-b whitespace-nowrap">
                                Purchase Amount
                            </th>
                            <th className="px-4 py-3 text-left text-sm font-semibold text-[#667085] border-b whitespace-nowrap">
                                Actions
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            <tr>
                                <td colSpan={5} className="text-center py-6 text-[#667085]">
                                    Loading parties...
                                </td>
                            </tr>
                        ) : currentDisplay.length === 0 ? (
                            <tr>
                                <td colSpan={5} className="text-center py-6 text-[#667085]">
                                    No data found.
                                </td>
                            </tr>
                        ) : (
                            currentDisplay.map(({ party, openingBalance, purchaseAmount, remainingAmount }) => (
                                <tr key={party.id} className="hover:bg-gray-50 transition">
                                    <td className="px-4 py-3 text-sm text-[#101828] border-b whitespace-nowrap">
                                        {party.name}
                                    </td>
                                    <td className="px-4 py-3 text-sm text-[#344054] font-semibold border-b whitespace-nowrap">
                                        ₹{typeof openingBalance === 'number' && !isNaN(openingBalance) ? openingBalance.toLocaleString('en-IN') : "0"}
                                    </td>
                                    <td className="px-4 py-3 text-sm text-[#344054] font-semibold border-b whitespace-nowrap">
                                        ₹{typeof remainingAmount === 'number' && !isNaN(remainingAmount) ? remainingAmount.toLocaleString('en-IN') : "0"}
                                    </td>
                                    <td className="px-4 py-3 text-sm text-[#344054] font-semibold border-b whitespace-nowrap">
                                        ₹{typeof purchaseAmount === 'number' && !isNaN(purchaseAmount) ? purchaseAmount.toLocaleString('en-IN') : "0"}
                                    </td>
                                    <td className="px-4 py-3 text-sm border-b whitespace-nowrap">
                                        <div className="flex flex-wrap gap-2">
                                            <button onClick={() => handleAddStock(party.id)} className="bg-green-500 text-white px-2 py-1 text-xs rounded hover:bg-green-700 transition">Add Stocks</button>
                                            <button onClick={() => handleSellStock(party.id)} className="bg-blue-500 text-white px-2 py-1 text-xs rounded hover:bg-blue-700 transition">Sell Stocks</button>
                                            <button onClick={() => handleWithdraw(party.id)} className="bg-yellow-500 text-white px-2 py-1 text-xs rounded hover:bg-yellow-700 transition">Withdraw Amount</button>
                                            <button onClick={() => handleAddAmount(party.id)} className="bg-indigo-500 text-white px-2 py-1 text-xs rounded hover:bg-indigo-700 transition">Add Amount</button>
                                            <button onClick={() => handleUpdateStockValue(party.id)} className="bg-orange-500 text-white px-2 py-1 text-xs rounded hover:bg-orange-700 transition">Update Stock Value</button>
                                            <button onClick={() => handleViewHistory(party.id)} className="bg-gray-500 text-white px-2 py-1 text-xs rounded hover:bg-gray-700 transition">View History</button>
                                            <button onClick={() => handleViewBalanceSheet(party.id)} className="bg-purple-500 text-white px-2 py-1 text-xs rounded hover:bg-purple-700 transition">View Balance Sheet</button>
                                            <button onClick={() => handleExport(party.id)} className="bg-pink-500 text-white px-2 py-1 text-xs rounded hover:bg-pink-700 transition">Export</button>
                                        </div>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>

            <div className="flex items-center justify-between mt-4 space-x-3 relative z-0">
                <span className="text-[#344054] text-[14px]">
                    Page {currentPage} of {Math.max(1, Math.ceil(filteredData.length / totalRows))}
                </span>
                <div className='flex gap-2'>
                    <button
                        disabled={currentPage === 1}
                        onClick={() => setCurrentPage(prev => prev - 1)}
                        className="px-3 py-1 bg-[#fff] disabled:opacity-50 text-[#344054] hover:bg-gray-300 transition border-[#D0D5DD] border-[1px] rounded-[6px] text-[14px]"
                    >
                        Previous
                    </button>
                    <button
                        disabled={
                            currentPage === Math.ceil(filteredData.length / totalRows) ||
                            filteredData.length === 0
                        }
                        onClick={() => setCurrentPage(prev => prev + 1)}
                        className="px-3 py-1 bg-[#fff] disabled:opacity-50 hover:bg-gray-300 transition border-[#D0D5DD] border-[1px] rounded-[6px] text-[14px] text-[#344054]  "
                    >
                        Next
                    </button>
                </div>
            </div>
        </div>
    );
}

export default Partylist;
